import * as React from 'react';
import { GitBranch, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import type { Scenario } from '../../../core/assistant/stores/scenariosStore';
import { useScenarioSaveAs } from '../hooks/useScenarioSaveAs';
import type { SuccessionSlateFact } from '../models/SuccessionPlanningPersistence';

export interface MasterChartPlanSeed {
  readonly successionSlates?: readonly SuccessionSlateFact[];
  /** Detached scenario built from the command that opened the prompt. */
  readonly scenario?: Scenario;
  /** Why this particular command cannot be represented in a planning chart. */
  readonly planningUnavailableReason?: string;
  /**
   * View recipe (`?review=` id) the gesture was made in — carried onto the
   * planning draft's URL so the mode the user was working in (e.g. succession
   * planning) is active when the new diagram opens.
   */
  readonly reviewId?: string;
}

/**
 * The fork-first prompt for the master chart.
 *
 * The default correct path for a what-if is a separate planning diagram —
 * forked from the chart, shared explicitly with the people who should see it,
 * applied back to the org when it stops being hypothetical. People who know
 * that create the diagram first. People who don't just start editing the
 * master chart, so their FIRST change gesture asks the question the product
 * would otherwise answer for them:
 *
 *   change the org, or plan separately?
 *
 * Asked once per chart per session: an existing open draft, or a prior
 * "change this chart" answer, means the question is settled and the gesture
 * proceeds without ceremony.
 */
export function useMasterChartChangeIntercept(input: {
  diagramId: string | null | undefined;
  /** Only live (master) charts intercept; planning diagrams are the answer. */
  isLiveChart: boolean;
  /** An open draft means the choice was already made — never re-ask mid-draft. */
  hasOpenDraft: () => boolean;
}): {
  /** Runs `proceed` directly, or after the user chooses to change this chart. */
  guardChange: (proceed: () => void, cancel?: () => void, planSeed?: MasterChartPlanSeed) => void;
  interceptDialog: React.ReactElement | null;
} {
  const { diagramId, isLiveChart, hasOpenDraft } = input;
  const [open, setOpen] = React.useState(false);
  const pendingRef = React.useRef<{
    proceed: () => void;
    cancel?: () => void;
    planSeed?: MasterChartPlanSeed;
  } | null>(null);
  const { saveAs, isSaving } = useScenarioSaveAs();
  const storageKey = diagramId ? `atlas-master-change-ack:${diagramId}` : null;

  const guardChange = React.useCallback((
    proceed: () => void,
    cancel?: () => void,
    planSeed?: MasterChartPlanSeed,
  ) => {
    if (!isLiveChart || hasOpenDraft()) {
      proceed();
      return;
    }
    if (storageKey && sessionStorage.getItem(storageKey) === 'direct') {
      proceed();
      return;
    }
    pendingRef.current = { proceed, cancel, planSeed };
    setOpen(true);
  }, [hasOpenDraft, isLiveChart, storageKey]);

  const chooseDirect = React.useCallback(() => {
    if (storageKey) sessionStorage.setItem(storageKey, 'direct');
    setOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.proceed();
  }, [storageKey]);

  const choosePlan = React.useCallback(async () => {
    const pending = pendingRef.current;
    if (pending?.planSeed?.planningUnavailableReason) return;
    pendingRef.current = null;
    pending?.cancel?.();
    // The source chart remains untouched, but the gesture that opened this
    // prompt must seed the destination plan. Otherwise "Plan separately"
    // creates an empty chart and silently loses the user's first decision.
    await saveAs('plan', pending?.planSeed);
    setOpen(false);
  }, [saveAs]);

  const planningUnavailableReason = pendingRef.current?.planSeed?.planningUnavailableReason;

  const dismiss = React.useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.cancel?.();
    setOpen(false);
  }, []);

  const interceptDialog = open ? (
    <Dialog open onOpenChange={(next) => { if (!next && !isSaving) dismiss(); }}>
      <DialogContent role="alertdialog" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Where should this change go?</DialogTitle>
          <DialogDescription>
            This is the main org chart. Edit it as a draft here, or try the
            change in a separate planning diagram.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" disabled={isSaving} onClick={dismiss}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSaving || Boolean(planningUnavailableReason)}
              title={planningUnavailableReason}
              data-testid="tid.orgchart.masterIntercept.plan"
              onClick={() => { void choosePlan(); }}
            >
              <GitBranch className="size-3.5" />
              {isSaving ? 'Creating…' : 'Plan separately'}
            </Button>
            <Button
              type="button"
              disabled={isSaving}
              data-testid="tid.orgchart.masterIntercept.direct"
              onClick={chooseDirect}
            >
              <Pencil className="size-3.5" />
              Edit as draft
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;

  return { guardChange, interceptDialog };
}
